<template>
  <DialogFrame
    v-model:visible="visible"
    :loading="loading"
    @onHide="onHide"
    @onClose="closeModal"
    class="p-dialog-breathe"
  >
    <template #title>Edit Cluster</template>

    <template #body>
      <div class="modal-body-add-cluster">
        <DialogClusterForm
          v-if="stage === 1"
          v-model:cluster="editCluster"
          v-model:newEvent="newEvent"
          @changeStage="goToStageTwo"
        />

        <DialogClusterZones
          v-if="stage === 2"
          v-model:cluster="editCluster"
          v-model:newEvent="newEvent"
          @changeStage="goToStageOne"
        />
      </div>
    </template>

    <template #footer>
      <Button
        v-if="stage === 1"
        :disabled="loading || !zoneValid"
        label="Save Changes"
        severity="secondary"
        @click="saveModal()"
        autofocus
      />

      <Button
        v-if="stage === 2"
        :disabled="loading"
        label="Done"
        severity="primary"
        @click="stage = 1"
        autofocus
      />
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import type { EventTypeNew, ZoneType, ClusterType } from '@/types/Event';

import { Button } from 'primevue';

import DialogFrame from './DialogFrame.vue';
import DialogClusterForm from './forms/DialogClusterForm.vue';
import DialogClusterZones from './forms/DialogClusterZones.vue';

const newEvent = defineModel<EventTypeNew>('newEvent');
const editCluster = defineModel<ClusterType>('editCluster');

// Create an initial state we can refer back to if we cancel changes.
const clusterInitialState = <ClusterType>JSON.parse(JSON.stringify(editCluster.value));

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    defaultZones?: ZoneType[];
    index: number;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits(['onClusterEdited', 'onClusterCanceled']);

const visible = ref(true);
const loading = ref(false);
const stage = ref(1);

watch(visible, (newVal) => {
  if (newVal === true) {
    if (props.defaultZones && editCluster.value) {
      editCluster.value.zones = props.defaultZones;
    }
  }
});

const saveModal = () => {
  emit('onClusterEdited', editCluster.value);
  stage.value = 1;
  visible.value = false;
};

const hasUniqueName = computed(() => {
  let nameUnique = true;

  // Trick it into ignoring itself when making the comparison
  if (newEvent.value?.clusters) {
    const clusters = [...newEvent.value.clusters];
    clusters.splice(props.index, 1);
    clusters.forEach((cluster) => {
      if (cluster.name.toLowerCase().trim() === editCluster.value?.name.toLowerCase().trim()) {
        nameUnique = false;
      }
    });
  }

  return nameUnique;
});

const zoneValid = computed(() => {
  if (editCluster.value) {
    const hasName = editCluster.value.name.trim() !== '';
    const hasType = editCluster.value.categories.length > 0;
    return hasName && hasType && hasUniqueName.value;
  }
  return false;
});

const onHide = () => {
  onClusterCanceled();
};

const closeModal = () => {
  onClusterCanceled();
  visible.value = false;
};

const onClusterCanceled = () => {
  if (newEvent.value?.clusters) {
    newEvent.value.clusters.splice(props.index, 1, clusterInitialState);
  }
  stage.value = 1;
  emit('onClusterCanceled');
};

const goToStageTwo = () => {
  stage.value = 2;
};

const goToStageOne = () => {
  stage.value = 1;
};
</script>
